// Keep the running app on the deployed build, without anyone being told to
// "fully close and reopen the app".
//
// WHY THIS EXISTS (2026-08-25). Julian reported a copy button missing from a
// code block for the third time. The button was present in the live bundle, the
// live CSS was correct, and the exact block rendered correctly at 390px against
// that CSS — the fix had shipped ~26 hours earlier. What had not happened was
// his phone loading it. index.html is served `no-cache, no-store` and the assets
// are content-hashed and immutable, so the caching is right; but a PWA left open
// never re-fetches index.html at all, so a session that stays open runs
// yesterday's JavaScript indefinitely. Every fix shipped in that window is
// invisible, and the only remedy was telling him to reopen the app — which is
// exactly the thing he asked never to be asked again.
//
// So: poll index.html, compare the module hash to the one actually running, and
// reload when it is safe. "Safe" is the whole design — a silent reload that eats
// a half-typed message or kills a live stream is worse than a stale button. When
// it is not safe, we wait; when it never becomes safe, a pill lets him choose.

const POLL_MS = 90_000;
/** Never reload within this of boot — avoids a loop if a deploy is mid-flight. */
const MIN_UPTIME_MS = 30_000;

const bootedAt = Date.now();
let pending = false;
let timer: number | null = null;
let onPending: (() => void) | null = null;

/** The module URL this page is actually executing, e.g. /assets/index-abc123.js */
function runningAsset(): string | null {
  // import.meta.url is the bundle's own URL — the single most reliable answer,
  // and it cannot go stale the way a DOM query can if the tag is rewritten.
  try {
    const u = new URL(import.meta.url);
    return u.pathname;
  } catch {
    const tag = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
    return tag ? new URL(tag.src, location.origin).pathname : null;
  }
}

/** The module URL the SERVER is currently handing out. */
async function deployedAsset(): Promise<string | null> {
  try {
    const res = await fetch(`/index.html?cb=${Date.now()}`, {
      cache: "no-store",
      credentials: "same-origin",
    });
    if (!res.ok) return null;
    const html = await res.text();
    return /src="(\/assets\/index-[A-Za-z0-9_-]+\.js)"/.exec(html)?.[1] ?? null;
  } catch {
    return null; // offline, or the box is restarting — try again next tick
  }
}

/**
 * Is it safe to reload right now?
 *
 * Deliberately conservative and deliberately NOT coupled to the store's shape:
 * anything unexpected returns false, so the worst case is a pill instead of a
 * silent reload. Losing a typed message to an "improvement" would be the exact
 * kind of thing this file is supposed to prevent.
 */
function safeToReload(): boolean {
  try {
    if (Date.now() - bootedAt < MIN_UPTIME_MS) return false;
    if (document.visibilityState !== "visible") return false;

    // A half-typed message anywhere on the page.
    for (const el of document.querySelectorAll("textarea, input[type='text']")) {
      if ((el as HTMLTextAreaElement | HTMLInputElement).value.trim()) return false;
    }
    // A reply still streaming in, or a modal/sheet mid-interaction.
    if (document.querySelector("[role='dialog']")) return false;

    const st = (window as unknown as { __RFLY_SESSION_STATE__?: () => unknown })
      .__RFLY_SESSION_STATE__?.() as { streamingMessageId?: string | null } | undefined;
    if (st && st.streamingMessageId) return false;

    return true;
  } catch {
    return false;
  }
}

async function check(): Promise<void> {
  if (!pending) {
    const running = runningAsset();
    const deployed = await deployedAsset();
    // Only act on a confident mismatch. A null on either side means we could not
    // tell, and guessing would reload the app for no reason.
    if (!running || !deployed || running === deployed) return;
    pending = true;
    onPending?.();
  }
  if (safeToReload()) location.reload();
}

/**
 * @param notify called once when a newer build exists but reloading is not yet
 *               safe, so the UI can offer the choice.
 */
export function startAutoUpdate(notify?: () => void): void {
  if (timer !== null) return;
  onPending = notify ?? null;
  timer = window.setInterval(() => void check(), POLL_MS);
  // The moment he comes back to the app is both the most likely time for a new
  // build to exist and the safest possible moment to apply one.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void check();
  });
  window.addEventListener("focus", () => void check());
  void check();
}

/**
 * The build actually executing, e.g. "Yt9dX16b" from /assets/index-Yt9dX16b.js.
 *
 * Derived at RUNTIME from the bundle's own URL rather than stamped in at build
 * time, because the deploy is not always run by the same thing that writes the
 * source — a build-time env var would have read "dev" in production and been
 * worse than nothing. This is the same value compared against the server, so
 * what it shows is by definition what is running.
 */
export function runningBuildId(): string {
  const p = runningAsset();
  return /index-([A-Za-z0-9_-]+)\.js$/.exec(p ?? "")?.[1] ?? "unknown";
}

/** True when a newer build is deployed and the app is still on the old one. */
export function updatePending(): boolean {
  return pending;
}
